import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../utils/apiClient";

/**
 * Send a multipart form with upload progress and a way to cancel it.
 *
 * While a send is in flight, closing or reloading the tab asks first: a
 * half-sent upload is work the person thinks they handed in.
 *
 * The Content-Type is passed explicitly, so axios leaves the header to the
 * browser, which adds the boundary.
 *
 *   const { send, cancel, progress, sending } = useUploadRequest();
 *   const data = await send("post", "/general-assignments/add", formData);
 */
export default function useUploadRequest() {
  const [progress, setProgress] = useState(null);
  const controller = useRef(null);
  const sending = progress !== null;

  useEffect(() => {
    if (!sending) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [sending]);

  useEffect(() => () => controller.current?.abort(), []);

  const send = useCallback(async (method, url, formData) => {
    controller.current = new AbortController();
    setProgress(0);
    try {
      const { data } = await apiClient.request({
        method,
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.current.signal,
        // Held at 99 until the server answers: the bytes are sent, the work
        // is not saved yet.
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        },
      });
      return data;
    } finally {
      controller.current = null;
      setProgress(null);
    }
  }, []);

  const cancel = useCallback(() => controller.current?.abort(), []);

  return { send, cancel, progress, sending };
}

export const isCancelled = (err) => err?.code === "ERR_CANCELED" || err?.name === "CanceledError";

/**
 * What to tell the person when a send fails. A cancel is not a failure (null).
 * No response at all means the connection dropped mid-upload — nothing was
 * saved, and saying so is what stops them assuming it went through.
 */
export function uploadFailure(err, fallback) {
  if (isCancelled(err)) return null;
  if (!err?.response) return "The connection dropped before the upload finished. Nothing was saved — try again.";
  return err.response.data?.message || fallback;
}
